import React from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import AtlasV2PaidTrafficV2PaidTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2PaidTrafficV2/AtlasV2PaidTrafficV2PaidTrafficSplineChart';
import AtlasV2PaidTrafficKeywordCountWithPaidTraffic from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2PaidTrafficV2/AtlasV2PaidTrafficKeywordCountWithPaidTraffic';
import AtlasV2PaidTrafficV2ShareOfTrafficSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2PaidTrafficV2/AtlasV2PaidTrafficV2ShareOfTrafficSplineChart';
import AtlasV2PaidTrafficV2ProductsWithTraffic from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2PaidTrafficV2/AtlasV2PaidTrafficV2ProductsWithTraffic';
import AtlasV2PaidTrafficDoubleDropdownTopEntity from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2PaidTrafficV2/AtlasV2PaidTrafficDoubleDropdownTopEntity';
import AtlasV2PaidTrafficV2AdSpendSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2PaidTrafficV2/AtlasV2PaidTrafficV2AdSpendSplineChart';
import {
  useBrandEntity,
  useBusinessUnitEntity,
  useCategoryEntity,
  useProductEntity,
  useSegmentEntity,
  useSubcategoryEntity
} from 'src/utils/Hooks/reduxSelectors';
import AtlasV2TotalFilterableStackedBarChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalFilterableStackedBarChart';
import { AtlasV2TrafficProductGrid } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2ProductGrid';
import AtlasV2FilterableStackedBarChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalFilterableStackedBarChart';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficV2BrandedUnbrandedSplineChart';
import { shouldShowBrandUnbranded } from 'src/utils/app';
import { useAtlasV2TabTreatmentRedirect } from 'src/components/AtlasV2Components/Hooks/useAtlasV2TabTreatmentRedirect';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
/**
 * Atlas V2 Paid Traffic V2 Page layout - Tab name: traffic subtab name: adClicks
 */
export const AtlasV2PaidTrafficV2PageLayout = () => {
  const { isCategoryEntity, isAllCategoriesEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isSegmentEntity } = useSegmentEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const { isBrandEntity } = useBrandEntity();
  const { isProductEntity } = useProductEntity();
  const { isRedirecting } = useAtlasV2TabTreatmentRedirect();
  if (isRedirecting) return null;

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        {/* ── Paid Search Traffic spline chart ── */}
        <ChartContextProvider group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC} chartName={CHART_NAMES.PAID_TRAFFIC_SPLINE}>
          <AtlasV2PaidTrafficV2PaidTrafficSplineChart />
        </ChartContextProvider>

        {/* ── Paid Search Spend spline chart ── */}
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC_AD_SPEND}
          chartName={CHART_NAMES.PAID_TRAFFIC_AD_SPEND_SPLINE}
        >
          <AtlasV2PaidTrafficV2AdSpendSplineChart />
        </ChartContextProvider>

        {/* ── Share of Paid Search Traffic spline chart ── */}
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC_SHARE}
          chartName={CHART_NAMES.PAID_TRAFFIC_SHARE_SPLINE}
        >
          <AtlasV2PaidTrafficV2ShareOfTrafficSplineChart />
        </ChartContextProvider>

        {/* ── Top Entity + Products with Paid Traffic ── */}
        {!isProductEntity && (
          <>
            <ChartContextProvider
              group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC_TOP_ENTITY}
              chartName={CHART_NAMES.PAID_TRAFFIC_TOP_ENTITY_COLUMN}
            >
              <AtlasV2PaidTrafficDoubleDropdownTopEntity />
            </ChartContextProvider>
            <ChartContextProvider
              group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC_PRODUCTS}
              chartName={CHART_NAMES.PAID_TRAFFIC_PRODUCTS_SPLINE}
            >
              <AtlasV2PaidTrafficV2ProductsWithTraffic />
            </ChartContextProvider>
          </>
        )}

        {/* ── Keyword Count with Paid Search Traffic ── */}
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC_KEYWORD_COUNT}
          chartName={CHART_NAMES.PAID_TRAFFIC_KEYWORD_COUNT_SPLINE}
        >
          <AtlasV2PaidTrafficKeywordCountWithPaidTraffic />
        </ChartContextProvider>

        {/* ── Branded/Unbranded Paid Traffic spline chart (shared component, hardcodes its own chartName) ── */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) &&
          shouldShowBrandUnbranded() && (
            <ChartContextProvider group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC_BRANDED_UNBRANDED}>
              <AtlasV2TotalTrafficV2BrandedUnbrandedSplineChart fieldName="adClicks" />
            </ChartContextProvider>
          )}

        {/* ── Filterable Stacked Bar (keyword) -- shared component, hardcodes its own chartName ── */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC_FILTERABLE_STACKED_BAR}>
            <AtlasV2TotalFilterableStackedBarChart
              metricDropdownOptions={[
                {
                  id: 'adClicks',
                  label: 'Paid Search Traffic',
                  apiMetricFieldName: 'adClicks'
                },
                {
                  id: 'adSpend',
                  label: 'Paid Search Spend',
                  apiMetricFieldName: 'adSpend',
                  metricTypeDisplay: METRICTYPE.MONEY
                },
                {
                  id: 'percentTraffic',
                  label: '% of Paid Search Traffic',
                  apiMetricFieldName: 'adClicks',
                  isPercentageVisible: true,
                  defaultMetric: 'adClicks'
                },
                {
                  id: 'percentSpend',
                  label: '% of Paid Search Spend',
                  apiMetricFieldName: 'adSpend',
                  isPercentageVisible: true,
                  defaultMetric: 'adSpend'
                }
              ]}
              groupByFieldOptions={[
                {
                  id: 'keyword',
                  label: 'Keyword'
                }
              ]}
            />
          </ChartContextProvider>
        )}

        {/* ── Brand → Category/Subcategory top entity chart ── */}
        {isBrandEntity && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC_TOP_ENTITY_BRAND}
            chartName={CHART_NAMES.PAID_TRAFFIC_TOP_ENTITY_COLUMN}
          >
            <AtlasV2PaidTrafficDoubleDropdownTopEntity
              groupByFields={[
                { id: 'category', label: 'Category', indexName: 'traffic' },
                { id: 'subcategory', label: 'Subcategory', indexName: 'traffic' }
              ]}
            />
          </ChartContextProvider>
        )}

        {/* ── Filterable Stacked Bar (category/subcategory) -- shared component, hardcodes its own chartName ── */}
        {(isCategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.PAID_TRAFFIC_FILTERABLE_STACKED_BAR_CATEGORY}>
            <AtlasV2FilterableStackedBarChart
              metricDropdownOptions={[
                {
                  id: 'adClicks',
                  label: 'Paid Search Traffic',
                  apiMetricFieldName: 'adClicks',
                  subtitleLabel: 'paid search traffic'
                },
                {
                  id: 'adSpend',
                  label: 'Paid Search Spend',
                  apiMetricFieldName: 'adSpend',
                  subtitleLabel: 'paid search spend',
                  metricTypeDisplay: METRICTYPE.MONEY
                },
                {
                  id: 'percentAdTraffic',
                  label: '% of Paid Search Traffic',
                  apiMetricFieldName: 'adClicks',
                  isPercentageVisible: true,
                  defaultMetric: 'adClicks',
                  subtitleLabel: 'percentage of paid search traffic'
                },
                {
                  id: 'percentAdSpend',
                  label: '% of Paid Search Spend',
                  apiMetricFieldName: 'adSpend',
                  isPercentageVisible: true,
                  defaultMetric: 'adSpend',
                  subtitleLabel: 'percentage of paid search spend'
                }
              ]}
              groupByFieldOptions={[
                ...(isAllCategoriesEntity || isSegmentEntity || isBusinessUnitEntity
                  ? [
                      {
                        id: 'category',
                        label: 'Category'
                      }
                    ]
                  : []),
                {
                  id: 'subcategory',
                  label: 'Subcategory'
                }
              ]}
            />
          </ChartContextProvider>
        )}

        <AtlasV2TrafficProductGrid trafficType="adClicks" />
      </Flex>
    </BasePageContainer>
  );
};
